import { CommentaryLanguages, PortfolioStudioPreferencesApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageCtxConsumer } from "$root/components/PageCtx";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { customObjectEntriesFn, qualifier } from "$root/utils";
import { useQueryReactor, useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form } from "@mdotm/shadcn/mdotm/components";
import { Text } from "@mdotm/shadcn/legacy/components";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usePortfolioStudioRedirect } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";

const ExplainabilitySettings = ({ active }: { active: boolean }): React.JSX.Element => {
	const { t } = useTranslation();
	const portfolioStudioPreferenceApi = useApiGen(PortfolioStudioPreferencesApiFactory);

	const { control, formState, handleSubmit, setValue, reset } = useForm({
		defaultValues: {
			commentaryLanguage: undefined as CommentaryLanguages | undefined,
		},
		resolver: zodResolver(
			z.object({
				commentaryLanguage: z.nativeEnum(CommentaryLanguages),
			}),
		),
	});

	const query = useQueryNoRefetch(["getExplainabilitySettings", active], {
		enabled: active,
		queryFn: () => axiosExtract(portfolioStudioPreferenceApi.getExplainabilitySettings()),
	});
	useQueryReactor(query, {
		onSuccess: ({ commentaryLanguage }) => setValue("commentaryLanguage", commentaryLanguage),
	});

	const onSubmit = handleSubmit(async (data) => {
		try {
			await portfolioStudioPreferenceApi.setExplainabilitySettings({
				commentaryLanguage: data.commentaryLanguage,
			});
			reset({ commentaryLanguage: data.commentaryLanguage });
		} catch (error) {
			platformToast({
				children: "Settings could not be saved. Please try again.",
				icon: "Settings",
				severity: "error",
			});
			throw error;
		}
	});

	const portfolioStudio = usePortfolioStudioRedirect();

	return (
		<>
			<Form onSubmitAsync={() => onSubmit()}>
				<div className="flex flex-wrap gap-x-8">
					<div>
						<LanguageIcon />
					</div>
					<div className="pt-2">
						<Text type="Body/L/Bold">Language</Text>
						<p className="mt-2 mb-2.5">Select the language in which you want the comments to be shown and generated.</p>

						<FormFields.Select
							control={control}
							name="commentaryLanguage"
							label="Language"
							className="w-[240px]"
							formState={formState}
							options={customObjectEntriesFn(CommentaryLanguages).map(([key, value]) => ({
								value,
								label: key,
							}))}
							data-qualifier={qualifier.explainabilitySettings.selectLanguage}
						/>
					</div>
				</div>

				{active && (
					<PageCtxConsumer>
						{(ctx) =>
							ctx.titleActionPortalTarget &&
							createPortal(
								<div className="flex items-center gap-2">
									<Button
										size="small"
										palette="tertiary"
										onClick={() => portfolioStudio.navigate()}
										data-qualifier={qualifier.explainabilitySettings.cancel}
									>
										{t("BUTTON.CANCEL")}
									</Button>
									<Button
										size="small"
										palette="primary"
										onClickAsync={() => onSubmit()}
										data-qualifier={qualifier.explainabilitySettings.save}
									>
										{t("BUTTON.SAVE")}
									</Button>
								</div>,
								ctx.titleActionPortalTarget,
							)
						}
					</PageCtxConsumer>
				)}
			</Form>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={formState.isDirty}
				pathToNotBlock={["/portfolio_studio_settings?tab=Explainability+options"]}
				showCancelIcon={false}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
};

export default ExplainabilitySettings;

function LanguageIcon() {
	return (
		<svg width="136" height="136" viewBox="0 0 136 136" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect width="136" height="136" fill="url(#pattern0_128_16622)" />
			<rect width="120" height="120" transform="translate(8 8)" fill="white" />
			<path
				d="M91.7489 49.0807C91.683 48.8969 91.5878 48.7279 91.4521 48.5926C86.6236 40.676 77.9312 35.3615 68.0005 35.3615C57.9802 35.3615 49.2203 40.7707 44.4186 48.8057C44.3668 48.8827 44.3099 48.9537 44.2755 49.0414C41.9042 53.1017 40.5215 57.8059 40.5215 62.8368C40.5215 67.8677 41.9039 72.5719 44.2749 76.633C44.3111 76.7252 44.3707 76.801 44.4261 76.8815C49.2289 84.9101 57.9854 90.3151 67.9996 90.3151C77.9294 90.3151 86.6211 85.0002 91.4504 77.0839C91.5872 76.948 91.6828 76.7779 91.7489 76.5928C94.1052 72.5414 95.4784 67.8505 95.4784 62.8365C95.4787 57.822 94.1056 53.1313 91.7489 49.0802L91.7489 49.0807ZM92.8111 61.536H78.3748C78.3101 57.8841 77.9809 54.2104 77.3853 50.8126H89.7631C91.5426 54.0207 92.6097 57.6641 92.8112 61.536H92.8111ZM88.0817 48.2136H76.8684C75.9613 44.2033 74.649 40.7746 72.9704 38.4601C79.1522 39.7188 84.4872 43.2919 88.0814 48.2136H88.0817ZM60.2271 61.536C60.2942 57.5733 60.6545 53.9538 61.2295 50.8126H74.7696C75.3446 53.9539 75.7048 57.5734 75.7719 61.536H60.2271ZM75.7728 64.1379C75.7058 68.1007 75.3455 71.7201 74.7705 74.8613H61.2304C60.6554 71.72 60.2951 68.1005 60.2281 64.1379H75.7728ZM68.0004 37.9601C70.2668 37.9601 72.689 41.8324 74.222 48.2142H61.779C63.3117 41.8324 65.734 37.9601 68.0004 37.9601ZM63.0302 38.4605C61.3514 40.7746 60.039 44.2036 59.1322 48.214H47.9189C51.5127 43.2918 56.8475 39.7186 63.0299 38.4605H63.0302ZM46.2372 50.8123H58.615C58.019 54.2104 57.6902 57.8839 57.6255 61.5356H43.1892C43.3907 57.6637 44.4578 54.0202 46.2373 50.8123H46.2372ZM43.189 64.1377H57.6254C57.6901 67.7896 58.0192 71.4633 58.6148 74.8611H46.237C44.4575 71.653 43.3908 68.0096 43.1892 64.1377H43.189ZM47.9205 77.4624H59.1323C60.0391 81.4706 61.3502 84.8978 63.0279 87.2128C56.8475 85.9544 51.5141 82.3828 47.9207 77.4624H47.9205ZM68.0003 87.7135C65.7341 87.7135 63.3123 83.8424 61.7795 77.4624H74.221C72.6882 83.8422 70.2663 87.7135 68.0001 87.7135H68.0003ZM72.9725 87.2127C74.6502 84.8983 75.9613 81.4711 76.8681 77.4623H88.0798C84.4858 82.3824 79.1528 85.9539 72.9727 87.2127H72.9725ZM89.764 74.861H77.3854C77.9814 71.4628 78.3102 67.7894 78.3749 64.1376H92.8112C92.6097 68.0096 91.5429 71.653 89.7637 74.861H89.764Z"
				fill="#94D0C4"
			/>
			<path
				d="M67.9998 27.7141C48.6338 27.7141 32.875 43.4699 32.875 62.8366C32.875 74.8817 38.8939 85.7929 48.9712 92.3065L43.8554 106.544C43.6653 107.07 43.8333 107.66 44.2711 108.006C44.5059 108.19 44.7916 108.285 45.077 108.285C45.3244 108.285 45.5718 108.212 45.7877 108.073L62.0272 97.4321C63.9888 97.7705 65.9917 97.9646 67.9994 97.9646C87.3654 97.9646 103.124 82.2057 103.124 62.8367C103.124 43.4691 87.3654 27.7134 67.9994 27.7134L67.9998 27.7141ZM67.9998 95.3632C65.979 95.3632 63.958 95.1761 61.9935 94.8086C61.9875 94.8071 61.9815 94.8074 61.9752 94.8062C61.9719 94.8056 61.9686 94.8056 61.9653 94.8047C61.9512 94.8023 61.9381 94.8074 61.924 94.8053C61.7692 94.7804 61.6119 94.7799 61.4562 94.8122C61.4197 94.82 61.3903 94.8421 61.3547 94.8529C61.2382 94.8877 61.1202 94.9188 61.0136 94.9889L47.6122 103.771L51.7525 92.2481C51.7663 92.2103 51.7594 92.172 51.7693 92.1339C51.7939 92.0393 51.8082 91.9483 51.8112 91.8524C51.8139 91.7737 51.8106 91.6991 51.7993 91.6221C51.7867 91.5386 51.7666 91.461 51.7385 91.3814C51.71 91.3002 51.6759 91.2265 51.6325 91.1529C51.5926 91.0858 51.5477 91.0259 51.4956 90.9657C51.4357 90.8962 51.3704 90.8369 51.2958 90.7812C51.2617 90.7558 51.2407 90.7189 51.2039 90.6962C41.3561 84.7443 35.4773 74.3286 35.4773 62.837C35.4773 44.9039 50.0654 30.3127 68 30.3127C85.9347 30.3127 100.523 44.9039 100.523 62.837C100.522 80.7732 85.9347 95.3636 67.9993 95.3636L67.9998 95.3632Z"
				fill="#C3C9D5"
			/>
			<path
				d="M24 37C25.6539 37 27 35.6539 27 34C27 32.3461 25.6539 31 24 31C22.3461 31 21 32.3461 21 34C21 35.6539 22.3461 37 24 37ZM24 33C24.5509 33 25 33.448 25 34C25 34.552 24.5509 35 24 35C23.4491 35 23 34.552 23 34C23 33.448 23.4491 33 24 33Z"
				fill="#C3C9D5"
			/>
			<path d="M23 27H25V29H23V27Z" fill="#C3C9D5" />
			<path d="M23 23H25V25H23V23Z" fill="#C3C9D5" />
			<path d="M23 19H25V21H23V19Z" fill="#C3C9D5" />
			<path
				d="M111 109C112.654 109 114 107.654 114 106C114 104.346 112.654 103 111 103C109.346 103 108 104.346 108 106C108 107.654 109.346 109 111 109ZM111 105C111.551 105 112 105.448 112 106C112 106.552 111.551 107 111 107C110.449 107 110 106.552 110 106C110 105.448 110.449 105 111 105Z"
				fill="#C3C9D5"
			/>
			<path d="M110 99H112V101H110V99Z" fill="#C3C9D5" />
			<path d="M110 95H112V97H110V95Z" fill="#C3C9D5" />
			<path d="M110 91H112V93H110V91Z" fill="#C3C9D5" />
			<path d="M23 95H25V97H23V95Z" fill="#C3C9D5" />
			<path d="M23 91H25V93H23V91Z" fill="#C3C9D5" />
			<path d="M23 87H25V89H23V87Z" fill="#C3C9D5" />
			<defs>
				<pattern id="pattern0_128_16622" patternContentUnits="objectBoundingBox" width="0.0294118" height="0.0294118">
					<use xlinkHref="#image0_128_16622" transform="scale(0.00735294)" />
				</pattern>
				<image
					id="image0_128_16622"
					width="4"
					height="4"
					xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAfSURBVHgBlcchAQAADAIwor/bFQHoAxLL3IDy1K0JA4lVC4UYicp2AAAAAElFTkSuQmCC"
				/>
			</defs>
		</svg>
	);
}
